import { Check, ChevronDown } from "lucide-react";
import type * as React from "react";
import { useState } from "react";
import { Button } from "#/components/ui/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "#/components/ui/command";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "#/components/ui/popover";
import { cn } from "#/lib/utils";

export type MultiSelectOption = {
	value: string;
	label: string;
	icon?: React.ReactNode;
};

type Props = {
	label: string;
	options: MultiSelectOption[];
	selected: string[];
	onToggle: (value: string) => void;
	searchable?: boolean;
	searchPlaceholder?: string;
	emptyText?: string;
};

export function MultiSelectFilter({
	label,
	options,
	selected,
	onToggle,
	searchable = false,
	searchPlaceholder = "Search...",
	emptyText = "No results.",
}: Props) {
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState("");
	const isActive = selected.length > 0;

	const displayed =
		searchable && search
			? options.filter((o) =>
					o.label.toLowerCase().includes(search.toLowerCase()),
				)
			: options;

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					size="sm"
					className={cn(
						"h-8 shrink-0 rounded-full px-3 text-xs font-medium gap-1.5",
						isActive
							? "border-primary bg-primary/10 text-primary-accessible hover:bg-primary/15 hover:text-primary-accessible"
							: "hover:border-foreground/30",
					)}
				>
					{label}
					{isActive && (
						<span className="flex size-4 items-center justify-center rounded-full bg-primary-accessible text-[10px] font-bold text-primary-foreground leading-none">
							{selected.length}
						</span>
					)}
					<ChevronDown
						className={cn(
							"size-3 transition-transform",
							open && "rotate-180",
							isActive ? "text-primary/70" : "text-muted-foreground",
						)}
					/>
				</Button>
			</PopoverTrigger>
			<PopoverContent
				align="start"
				sideOffset={6}
				className="w-auto p-0 min-w-[180px]"
			>
				<Command shouldFilter={false}>
					{searchable && (
						<CommandInput
							placeholder={searchPlaceholder}
							value={search}
							onValueChange={setSearch}
						/>
					)}
					<CommandList className="max-h-60">
						{searchable && <CommandEmpty>{emptyText}</CommandEmpty>}
						<CommandGroup>
							{displayed.map((option) => (
								<CommandItem
									key={option.value}
									value={option.value}
									onSelect={() => onToggle(option.value)}
								>
									{option.icon}
									<span className="flex-1">{option.label}</span>
									<Check
										className={cn(
											"ml-2 size-3.5 shrink-0",
											selected.includes(option.value)
												? "opacity-100"
												: "opacity-0",
										)}
									/>
								</CommandItem>
							))}
						</CommandGroup>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	);
}
